import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MessageIcon from '@mui/icons-material/Message';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Avatar, Box, Button, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import React from 'react';
import { BookingStatus } from '../../../../happypaws-shared/src/Booking/models/Booking';
import { SitterBookingWithDetails } from '../../../../happypaws-shared/src/Booking/models/BookingWithDetails';
import {
  ServiceType,
  SERVICE_TYPE_LABELS,
} from '../../../../happypaws-shared/src/Services/services';

interface BookingCardProps {
  booking: SitterBookingWithDetails;
  onAccept?: (id: string) => void;
  onDecline?: (id: string) => void;
  onView?: (id: string) => void;
  onMessage?: (id: string) => void;
  onCancel?: (id: string) => void;
  onReview?: (id: string) => void;
}

const getStatusConfig = (status: BookingStatus) => {
  switch (status) {
    case BookingStatus.PENDING:
      return { label: 'Pending', color: '#FFA726', bgColor: '#FFF3E0' };
    case BookingStatus.CONFIRMED:
      return { label: 'Confirmed', color: '#66BB6A', bgColor: '#E8F5E9' };
    case BookingStatus.COMPLETED:
      return { label: 'Completed', color: '#9E9E9E', bgColor: '#F5F5F5' };
    case BookingStatus.CANCELLED:
      return { label: 'Cancelled', color: '#D32F2F', bgColor: '#FFEBEE' };
    default:
      return { label: 'Unknown', color: '#9E9E9E', bgColor: '#F5F5F5' };
  }
};

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onAccept,
  onDecline,
  onView,
  onMessage,
  onCancel,
  onReview,
}) => {
  const statusConfig = getStatusConfig(booking.status as BookingStatus);
  const clientName = `${booking.clientFirstName} ${booking.clientLastName}`;
  const formatServiceName = (s: string) => SERVICE_TYPE_LABELS[s as ServiceType] ?? s;

  const renderActions = () => {
    switch (booking.status) {
      case BookingStatus.PENDING:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => onDecline?.(booking.id)}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => onAccept?.(booking.id)}
              sx={{ backgroundColor: '#178B8E', '&:hover': { backgroundColor: '#0F6164' } }}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onView?.(booking.id)}
            >
              View
            </Button>
          </Box>
        );
      case BookingStatus.CONFIRMED:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<MessageIcon />}
              onClick={() => onMessage?.(booking.id)}
              sx={{ backgroundColor: '#178B8E', '&:hover': { backgroundColor: '#0F6164' } }}
            >
              Message
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onView?.(booking.id)}
            >
              View
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => onCancel?.(booking.id)}
            >
              Cancel
            </Button>
          </Box>
        );
      case BookingStatus.COMPLETED:
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onView?.(booking.id)}
            >
              View
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<RateReviewIcon />}
              onClick={() => onReview?.(booking.id)}
              sx={{ backgroundColor: '#178B8E', '&:hover': { backgroundColor: '#0F6164' } }}
            >
              Leave Review
            </Button>
          </Box>
        );
      case BookingStatus.CANCELLED:
        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => onView?.(booking.id)}
          >
            View
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: '4px solid',
        borderLeftColor: statusConfig.color,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Avatar
              src={booking.pets[0]?.photoUrl ?? undefined}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: '#178B8E',
                fontSize: '1.5rem',
                fontWeight: 600,
              }}
            >
              {booking.pets[0]?.petName?.charAt(0) ?? 'P'}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {booking.pets.map((p) => p.petName).join(', ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Owner: {clientName}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={statusConfig.label}
            sx={{
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              fontWeight: 600,
              border: `1px solid ${statusConfig.color}`,
            }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Details */}
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary">
              Service
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatServiceName(booking.serviceType)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Dates
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {booking.startDate} — {booking.endDate}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Price
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
              ${booking.totalPrice}
            </Typography>
          </Box>
        </Box>

        {renderActions()}
      </CardContent>
    </Card>
  );
};

export default BookingCard;
